/**
 * The combat camera. ARCHITECTURE.md §7.
 *
 * Rules this implements, in priority order:
 *   1. both fighters stay readable — framing follows the pair, not one fighter
 *   2. the combat axis stays stable — the camera never crosses the axis, so left stays left
 *   3. distance adjusts smoothly — no snapping, no per-frame jitter
 *   4. every shake has a reason — impulses come from `camera:impulse` events only, and there is
 *      no ambient shake anywhere in this file
 *
 * Camera state is presentation-only: it is written in `update`/`lateUpdate` from interpolated
 * fighter transforms, never in `fixedUpdate`.
 */

import * as THREE from 'three';
import { clamp, lerp, damp, STAGE_HALF_WIDTH, FIXED_STEP } from '../core/index.js';

/** Camera framing constants, metres. */
const HEIGHT_BASE = 1.62;
const DIST_MIN = 4.4;
const DIST_MAX = 7.6;
/** Extra distance per metre of fighter separation. */
const DIST_PER_GAP = 0.72;
/** How far the camera may drift off the stage centre, so the wall stays in frame. */
const PAN_LIMIT = STAGE_HALF_WIDTH - 1.5;
/** Vertical offset applied when a fighter is airborne, so juggles stay framed. */
const AIR_LIFT = 0.42;

export class CameraDirector {
  constructor() {
    this.camera = null;
    /** Smoothed framing state. */
    this.centreX = 0;
    this.centreY = HEIGHT_BASE;
    this.distance = 5.6;
    this.yaw = 0;
    /** Active impulse: decaying positional offset plus optional oscillation. */
    this._imp = {
      active: false, ticks: 0, total: 0, strength: 0,
      dx: 0, dy: 0, dz: 0, shakeHz: 0, zoom: 0, kind: '',
    };
    /** Cinematic override, driven by `camera:cinematic`. */
    this._cine = { active: false, shot: '', subject: 0, target: 0, ticks: 0, total: 0 };
    this._offset = new THREE.Vector3();
    this._lookAt = new THREE.Vector3();
    this._unsubs = [];
    /** Fixed-camera mode for training and reproducible captures. */
    this.locked = false;
    this.lockedPose = { x: 0, y: 1.7, z: 6.0, targetX: 0, targetY: 1.3 };
    /** Phase accumulator for shake, advanced by simulation time so captures reproduce. */
    this._shakePhase = 0;
  }

  init(ctx) {
    const cam = new THREE.PerspectiveCamera(
      38,
      ctx.render.width / Math.max(1, ctx.render.height),
      0.1,
      420,
    );
    cam.position.set(0, HEIGHT_BASE, this.distance);
    cam.lookAt(0, 1.25, 0);
    this.camera = cam;
    ctx.render.camera = cam;

    this._unsubs.push(ctx.events.on('camera:impulse', (p) => this._onImpulse(p, ctx)));
    this._unsubs.push(ctx.events.on('camera:cinematic', (p) => this._onCinematic(p)));
    return cam;
  }

  _onImpulse(p, ctx) {
    const scale = ctx.config.get('cameraShakeScale');
    if (scale <= 0) return;
    const imp = this._imp;
    const strength = p.strength * scale;
    // A weaker impulse never interrupts a stronger one still playing — otherwise a jab during a
    // wall splat would flatten the splat's camera response.
    if (imp.active && imp.strength > strength && imp.ticks > 0) return;
    imp.active = true;
    imp.kind = p.kind;
    imp.strength = strength;
    imp.total = Math.max(1, p.durationTicks);
    imp.ticks = imp.total;
    imp.dx = p.dirX;
    imp.dy = p.dirY;
    imp.dz = p.dirZ;
    imp.shakeHz = p.shakeHz;
    imp.zoom = p.zoom;
  }

  _onCinematic(p) {
    const c = this._cine;
    if (p.phase === 'end') {
      c.active = false;
      return;
    }
    c.active = true;
    c.shot = p.shot;
    c.subject = p.subject;
    c.target = p.target;
    c.total = Math.max(1, p.durationTicks);
    c.ticks = c.total;
  }

  /**
   * Advances the camera. Called from `update` with the interpolated fighter transforms.
   * @param {number} dt seconds
   */
  update(dt, ctx) {
    const cam = this.camera;
    if (!cam) return;
    const fighters = ctx.sim.fighters;
    if (fighters.length !== 2) return;

    const alpha = ctx.time.alpha;
    const ax = lerp(fighters[0].prevX, fighters[0].x, alpha);
    const bx = lerp(fighters[1].prevX, fighters[1].x, alpha);
    const ay = lerp(fighters[0].prevY, fighters[0].y, alpha);
    const by = lerp(fighters[1].prevY, fighters[1].y, alpha);
    const az = lerp(fighters[0].prevZ, fighters[0].z, alpha);
    const bz = lerp(fighters[1].prevZ, fighters[1].z, alpha);

    if (this.locked) {
      const p = this.lockedPose;
      cam.position.set(p.x, p.y, p.z);
      this._lookAt.set(p.targetX, p.targetY, 0);
      cam.lookAt(this._lookAt);
      this._decayImpulse(ctx);
      return;
    }

    // --- framing target ---
    const t = this._computeTargets(ax, bx, ay, by);

    // --- smoothing: exponential, so it is frame-rate independent ---
    this.centreX = damp(this.centreX, t.x, 6.5, dt || FIXED_STEP);
    this.centreY = damp(this.centreY, t.y, 5.0, dt || FIXED_STEP);
    this.distance = damp(this.distance, t.dist, 4.0, dt || FIXED_STEP);

    // --- depth: follow the pair's average sidestep so 3D movement reads ---
    const midZ = (az + bz) * 0.5;
    const zOffset = clamp(midZ * 0.35, -0.7, 0.7);

    // --- impulse offset ---
    const imp = this._imp;
    let ox = 0;
    let oy = 0;
    let oz = 0;
    let zoom = 0;
    if (imp.active) {
      const t = imp.ticks / imp.total; // 1 -> 0
      const decay = t * t; // quadratic falloff settles without a visible stop
      const push = imp.strength * decay * 0.16;
      ox = imp.dx * push;
      oy = imp.dy * push;
      oz = imp.dz * push;
      if (imp.shakeHz > 0) {
        // Shake phase advances on SIMULATION time so a capture reproduces exactly.
        const s = Math.sin(this._shakePhase * imp.shakeHz * Math.PI * 2) * imp.strength * decay * 0.05;
        oy += s;
        ox += s * 0.4;
      }
      zoom = imp.zoom * decay;
    }
    this._shakePhase = ctx.time.simSeconds;
    this._decayImpulse(ctx);

    // --- cinematic offset: a tighter, lower angle on the acting fighter ---
    let cineBlend = 0;
    let cineX = 0;
    const cine = this._cine;
    if (cine.active) {
      cine.ticks--;
      if (cine.ticks <= 0) cine.active = false;
      const t = clamp(1 - cine.ticks / cine.total, 0, 1);
      // Ease in and out so entering and leaving a throw shot is never a cut.
      cineBlend = Math.sin(clamp(t, 0, 1) * Math.PI) * 0.75;
      const subj = fighters[cine.subject];
      cineX = subj ? lerp(subj.prevX, subj.x, alpha) : 0;
    }

    const finalX = lerp(this.centreX, cineX, cineBlend * 0.5);
    const finalDist = lerp(this.distance, this.distance * 0.72, cineBlend) * (1 - zoom);
    const finalY = lerp(this.centreY, this.centreY * 0.88, cineBlend);

    cam.position.set(finalX + ox, finalY + oy, finalDist + zOffset + oz);
    // Look slightly below the camera height so the fighters sit in the upper-middle of frame,
    // which keeps the floor and their feet visible — feet contact is a readability requirement.
    this._lookAt.set(finalX + ox * 0.5, finalY - 0.34, 0);
    cam.lookAt(this._lookAt);
  }

  /** The framing the camera is smoothing toward. Shared by `update` and `snapTo`. */
  _computeTargets(ax, bx, ay, by) {
    const midX = (ax + bx) * 0.5;
    const gap = Math.abs(bx - ax);
    const highest = Math.max(ay, by);
    return {
      x: clamp(midX, -PAN_LIMIT, PAN_LIMIT),
      y: HEIGHT_BASE + Math.min(highest, 2.2) * AIR_LIFT,
      dist: clamp(DIST_MIN + gap * DIST_PER_GAP, DIST_MIN, DIST_MAX),
    };
  }

  _decayImpulse() {
    const imp = this._imp;
    if (!imp.active) return;
    imp.ticks--;
    if (imp.ticks <= 0) {
      imp.active = false;
      imp.strength = 0;
    }
  }

  /** Training / capture mode: pin the camera so nothing moves between shots. */
  lock(pose) {
    this.locked = true;
    if (pose) Object.assign(this.lockedPose, pose);
  }

  unlock() {
    this.locked = false;
  }

  /**
   * Snaps the camera exactly onto its framing target and clears every transient.
   *
   * This must ASSIGN the target, not iterate the damping toward it. Exponential smoothing
   * converges asymptotically, so iterating leaves a residual that depends on where the camera
   * started — which made two runs of the same capture scenario differ by ~0.03 m of distance and
   * produced different pixels. A capture has to be exact, so it snaps.
   */
  snapTo(ctx) {
    this._imp.active = false;
    this._imp.ticks = 0;
    this._imp.strength = 0;
    this._imp.kind = '';
    this._cine.active = false;
    this._cine.ticks = 0;
    this._shakePhase = ctx.time.simSeconds;

    const fighters = ctx.sim.fighters;
    if (fighters.length === 2) {
      const t = this._computeTargets(fighters[0].x, fighters[1].x, fighters[0].y, fighters[1].y);
      this.centreX = t.x;
      this.centreY = t.y;
      this.distance = t.dist;
    }
    // One update with dt 0 writes the pose without advancing any smoothing.
    this.update(0, ctx);
  }

  /** @deprecated use snapTo — kept so existing callers do not silently keep the old behaviour. */
  settle(ctx) {
    this.snapTo(ctx);
  }

  state() {
    return {
      x: +this.camera.position.x.toFixed(4),
      y: +this.camera.position.y.toFixed(4),
      z: +this.camera.position.z.toFixed(4),
      distance: +this.distance.toFixed(4),
      locked: this.locked,
      impulse: this._imp.active ? this._imp.kind : '',
      cinematic: this._cine.active ? this._cine.shot : '',
    };
  }

  dispose() {
    for (const off of this._unsubs) off();
    this._unsubs.length = 0;
    this.camera = null;
  }
}
